// Client-side mirror of backend/utils/orderBreakdown.js (CRA cannot import from
// backend/). Produces the same ordered price-breakdown rows — coupons, rule
// offer, monthly volume discount, shipping, VAT, applied balance, rounding —
// that order emails, the invoice PDF and the checkout engine render. This is the
// single source for WHICH rows appear and in what order; each surface owns its
// own markup.
//
// Row shape: { key, kind, sign, amount, label, free? }
//  - kind: 'subtotal' | 'discount' | 'refund' | 'shipping' | 'vat' | 'balance' | 'rounding'
//  - sign: -1 (deduction) or +1 (addition)
//  - amount: positive magnitude of the row value
//  - free: true only for a zero-cost shipping line (renderers show "FREE")
//
// All input amounts are NET (prices are stored VAT-exclusive); the rounded total
// is GROSS (net subtotal - net discounts + net shipping + VAT). Net (business)
// mode emits a net subtotal row and a VAT row so every breakdown reconciles to
// the gross rounded total. Retail (gross) mode grosses NET amounts up at the
// dominant rate and shows VAT as a footnote (handled by each caller), so no VAT
// row is emitted there.
// Ledger rows carry the append they were granted on, so a running invoice with
// several batches can show "Offer Discount (12.09.2026)" instead of N identical
// rows. Mirrors backend/utils/orderBreakdown.js.

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// A negative quantity is money back (a refund line), not goods. Split the lines so
// callers can keep credits out of every goods-based figure — the discount pro-rata
// bases, the shipping dominant rate and the ordered-pieces progress — exactly as
// the backend engines do.
export function splitByCredit(items = []) {
  const goods = [];
  const credits = [];
  for (const item of items) {
    (Number(item?.quantity) < 0 ? credits : goods).push(item);
  }
  return { goods, credits };
}

// The refund total in the caller's display basis: NET for the business view, the
// centime-rounded GROSS at each line's own VAT rate for retail. Negative, since it
// reduces the bill. Matches the engine's refund-line money.
export function creditAmountFromItems(items = [], { vatExclusive = false } = {}) {
  const { credits } = splitByCredit(items);
  return round2(credits.reduce((sum, item) => {
    const rate = Number(item?.vat_rate) || 0;
    const unit = Number(item?.unit_price_final ?? item?.price ?? 0) || 0;
    const perUnit = vatExclusive ? unit : round2(unit * (1 + rate / 100));
    return sum + perUnit * (Number(item?.quantity) || 0);
  }, 0));
}
function formatShortDate(iso, lang) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat(lang, { day: '2-digit', month: '2-digit', year: 'numeric' })
      .format(new Date(iso));
  } catch {
    return String(iso).slice(0, 10);
  }
}

function resolveDiscountRowLabel(row, lang, t) {
  const percent = row.value != null ? ` (${Number(row.value)}%)` : '';
  if (row.kind === 'volume') {
    return { label: `${t('volumeDiscount', 'Monthly Vol. Discount')}${percent}`, amount: Math.abs(Number(row.amount_net) || 0) };
  }
  if (row.rule_type === 'min_order_discount') {
    return { label: `${t('minOrderDiscount', 'Min. Order Discount')}${percent}`, amount: Math.abs(Number(row.amount_net) || 0) };
  }
  const date = formatShortDate(row.at, lang);
  return {
    label: `${t('offerDiscount', 'Offer Discount')}${date ? ` (${date})` : ''}`,
    amount: Math.abs(Number(row.amount_net) || 0),
  };
}

export function buildBreakdownRows({
  t = (k) => k,
  lang = 'en',
  couponDiscounts = [],
  couponDiscount = 0,
  ruleDiscount = 0,
  shippingCost = 0,
  shippingFree = false,
  isPickup = false,
  hideShipping = false,
  volumeDiscount = 0,
  volumeDiscountPercentage = null,
  // A running (ongoing) order's discount ledger. When present it REPLACES the
  // single rule/volume rows: each entry is one discount actually granted, kept
  // separately so a running invoice can list them individually.
  discountRows = null,
  // Cart-level discount the admin applied to the whole order, as a PERCENT over
  // `cartDiscountBase` (the merchandise net after the manual per-line discounts
  // and any ledger discounts). Resolved last, so it renders after them.
  cartDiscountPercent = 0,
  cartDiscountBase = null,
  // Money back from refund (negative-quantity) lines, in the caller's display
  // basis — see creditAmountFromItems. Negative: it reduces the bill.
  creditAmount = 0,
  appliedBalance = 0,
  roundOffDiff = 0,
  ruleDiscountLabel = null,
  vatExclusive = false,
  netSubtotal = 0,
  netShipping = 0,
  vatOnDiscount = 0,
  vatAmount = 0,
}) {
  const rows = [];
  const push = (key, kind, sign, amount, label, extra = {}) => {
    rows.push({ key, kind, sign, amount, label, ...extra });
  };

  if (vatExclusive) {
    push('subtotal', 'subtotal', 1, Number(netSubtotal || 0), t('subtotal', 'Subtotal'));
  }

  const couponList = Array.isArray(couponDiscounts) && couponDiscounts.length > 0
    ? couponDiscounts
    : (Number(couponDiscount) > 0
        ? [{ code: null, rule_type: 'fixed', value: Number(couponDiscount), computed_discount: Number(couponDiscount) }]
        : []);

  for (const c of couponList) {
    const valueLabel = c.rule_type === 'percentage'
      ? ` (${c.value}%)`
      : ` (CHF ${Number(c.value).toFixed(2)})`;
    push(
      c.code ? `coupon-${c.code}` : 'coupon',
      'discount',
      -1,
      Number(c.computed_discount) || 0,
      `${c.code ? `${t('coupon', 'Coupon')} ${c.code}` : t('couponDiscount', 'Coupon Discount')}${valueLabel}`
    );
  }

  const hasLedger = Array.isArray(discountRows) && discountRows.length > 0;
  if (hasLedger) {
    for (const row of discountRows) {
      const { label, amount } = resolveDiscountRowLabel(row, lang, t);
      if (amount > 0) push(row.key || `ledger-${rows.length}`, 'discount', -1, amount, label);
    }
  } else {
    if (Number(ruleDiscount) > 0) {
      push('rule', 'discount', -1, Number(ruleDiscount), ruleDiscountLabel ?? t('offerDiscount', 'Offer Discount'));
    }

    if (Number(volumeDiscount) > 0) {
      const label = volumeDiscountPercentage != null
        ? `${t('volumeDiscount', 'Monthly Vol. Discount')} (${volumeDiscountPercentage}%)`
        : t('volumeDiscount', 'Monthly Vol. Discount');
      push('volume', 'discount', -1, Number(volumeDiscount), label);
    }
  }

  const cartPct = Math.min(100, Math.max(0, Number(cartDiscountPercent) || 0));
  if (cartPct > 0) {
    const cartBase = cartDiscountBase != null
      ? Math.max(0, Number(cartDiscountBase) || 0)
      : Math.max(0, Number(netSubtotal) - Number(ruleDiscount) - Number(volumeDiscount));
    const amount = round2(cartBase * (cartPct / 100));
    if (amount > 0) {
      push(
        'cart-discount',
        'discount',
        -1,
        amount,
        `${t('cartDiscount', 'Cart Discount')} (${round2(cartPct)}%)`
      );
    }
  }

  // Money back entered as negative lines. It reduces the bill, so it renders with
  // the deductions, after the discounts it did not take part in.
  if (Number(creditAmount) < 0) {
    push('refund', 'refund', -1, round2(Math.abs(Number(creditAmount))), t('refund', 'Refund'));
  }

  const isShippingLineFree = shippingFree || Number(shippingCost) === 0;
  if (!isPickup && (!hideShipping || isShippingLineFree)) {
    push(
      'shipping',
      'shipping',
      1,
      isShippingLineFree ? 0 : Number(netShipping || shippingCost || 0),
      t('shipping', 'Shipping'),
      { free: isShippingLineFree }
    );
  }

  // Business (vatExclusive) shows VAT as a row so the breakdown reconciles to
  // the gross rounded total.  Retail shows VAT as a separate footnote below the
  // breakdown (handled by each caller), so no row here.
  if (vatExclusive && Number(vatAmount) > 0) {
    push('vat', 'vat', 1, Number(vatAmount), t('vat', 'VAT'));
  }

  if (Number(appliedBalance) > 0) {
    push('balance', 'balance', -1, Number(appliedBalance), t('accountCreditApplied', 'Account Credit Applied'));
  } else if (Number(appliedBalance) < 0) {
    push('balance', 'balance', 1, Math.abs(Number(appliedBalance)), t('previousBalanceDue', 'Previous Balance Due'));
  }

  // Skip the row when the difference rounds to 0.00 — float noise (e.g. a
  // 2.8e-15 diff) must never render a "CHF 0.00" rounding line.
  if (Math.round(Number(roundOffDiff) * 100) !== 0) {
    push(
      'rounding',
      'rounding',
      Number(roundOffDiff) > 0 ? 1 : -1,
      Math.abs(Number(roundOffDiff)),
      t('roundingDiff', 'Rounding difference')
    );
  }

  return rows;
}
